import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../services/authService.js';
import { useToast } from './ToastContext.jsx';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const toast = useToast();
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('cyber_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('cyber_token') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      authApi.setToken(token);
      localStorage.setItem('cyber_token', token);
    } else {
      localStorage.removeItem('cyber_token');
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('cyber_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('cyber_user');
    }
  }, [user]);

  const handleError = (message) => {
    setError(message);
    if (message) toast?.push(message, { type: 'error' });
  };

  const login = async (payload) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.login(payload);
      setToken(data.token);
      setUser(data.user);
      toast?.push('Login successful', { type: 'success' });
      return data;
    } catch (err) {
      handleError(err?.response?.data?.message || err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.register(payload);
      setToken(data.token);
      setUser(data.user);
      toast?.push('Signup completed', { type: 'success' });
      return data;
    } catch (err) {
      handleError(err?.response?.data?.message || err.message || 'Signup failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken('');
    setUser(null);
    toast?.push('Logged out successfully', { type: 'info' });
  };

  const sendOtp = async (payload) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.sendOtp(payload);
      toast?.push(data.message || 'OTP sent', { type: 'success' });
      return data;
    } catch (err) {
      handleError(err?.response?.data?.message || err.message || 'OTP send failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (payload) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.verifyOtp(payload);
      if (data.token) setToken(data.token);
      if (data.user) setUser(data.user);
      toast?.push(data.message || 'OTP verified', { type: 'success' });
      return data;
    } catch (err) {
      handleError(err?.response?.data?.message || err.message || 'OTP verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (payload) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.forgotPassword(payload);
      toast?.push(data.message || 'Reset link sent', { type: 'success' });
      return data;
    } catch (err) {
      handleError(err?.response?.data?.message || err.message || 'Forgot password failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (payload) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.resetPassword(payload);
      toast?.push(data.message || 'Password reset successful', { type: 'success' });
      return data;
    } catch (err) {
      handleError(err?.response?.data?.message || err.message || 'Reset failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({ user, token, loading, error, login, register, logout, sendOtp, verifyOtp, forgotPassword, resetPassword }),
    [user, token, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
